import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { debounceTime, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';

import { trackByProp } from '@recruitee/common';
import type { Department } from '@recruitee/departments-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { AssignedLocationsInfoContainerComponent } from '@recruitee/locations';
import type { Requisition } from '@recruitee/requisitions-types';
import {
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ButtonLoadingDirective,
  ConnectedOverlayDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  EmptyStateComponent,
  FormFieldComponent,
  IconComponent,
  InputComponent,
  OverlayOriginDirective,
  RadioComponent,
  RtTooltip,
} from '@recruitee/user-interface';

import { RequisitionsStatusBadgeComponent } from '../../components/requisitions-status-badge/requisitions-status-badge.component';
import { statusCompare } from '../../helpers/status-compare';
import { RequisitionsCommonTextsService } from '../../services/requisitions-common-texts.service';

export type DuplicateRequisitionDialogData = {
  requisitions: Requisition[];
  departments: Department[];
  saveHandler: (id: number) => {};
};

type DialogResult = {
  goBack: boolean;
};

type RequisitionListItem = Requisition & {
  departmentName: string;
  jobOpeningsText: string;
  statusName: string;
  locationText: string;
};

type SortBy = 'title' | 'statusName';

@Component({
  selector: 'rt-requisitions-duplicate-requisition-dialog',
  templateUrl: './duplicate-requisition-dialog.component.html',
  styleUrls: ['./duplicate-requisition-dialog.component.less'],
  imports: [
    BadgeComponent,
    BoxComponent,
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    ButtonLoadingDirective,
    CommonModule,
    ConnectedOverlayDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    EmptyStateComponent,
    FormFieldComponent,
    IconComponent,
    InputComponent,
    OverlayOriginDirective,
    RadioComponent,
    RequisitionsStatusBadgeComponent,
    TranslatePipe,
    ReactiveFormsModule,
    RtTooltip,
    AssignedLocationsInfoContainerComponent,
  ],
})
export class DuplicateRequisitionDialogComponent
  implements DialogComponentI<DuplicateRequisitionDialogData, DialogResult>
{
  public visibleItems: RequisitionListItem[] = [];
  public selectedId: number | null;
  public queryControl = new FormControl('', { nonNullable: true });
  public saving = false;
  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public sortDropdownOpen = false;
  public sortBy: SortBy = 'title';
  public sortByLabels = {
    title: this.i18n.translate('app.offers.sort_by.title'),
    statusName: this.i18n.translate('app.offers.sort_by.status'),
  };

  private allItems: RequisitionListItem[] = [];
  private departmentMap: Record<number, Department>;
  private ngOnDestroy$ = new Subject<void>();
  private saveHandler: (id: number) => {};

  constructor(
    private dialogRef: DialogRef,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private requisitionsCommonTexts: RequisitionsCommonTextsService,
  ) {}

  public ngOnInit() {
    this.queryControl.valueChanges
      .pipe(
        debounceTime(100),
        map(query => query.toLowerCase()),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(query => this.setVisibleItems(query));
  }

  public setDialogData(data: DuplicateRequisitionDialogData, triggerUpdate: boolean) {
    this.departmentMap = data.departments.reduce(
      (map, department) => ({
        ...map,
        [department.id]: department,
      }),
      {},
    );
    this.saveHandler = data.saveHandler;
    this.allItems = data.requisitions
      .map(req => this.fillDescriptionFragments(req))
      .sort((a, b) => a.title.localeCompare(b.title));
    this.setVisibleItems();
  }

  public selectItem(id: number) {
    this.selectedId = id === this.selectedId ? null : id;
    this.cdRef.detectChanges();
  }

  public save() {
    if (this.selectedId) {
      this.saving = true;
      this.cdRef.detectChanges();
      this.saveHandler(this.selectedId);
    }
  }

  public goBack() {
    this.dialogRef.close({ goBack: true });
  }

  public cancel() {
    this.dialogRef.close();
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public toggleSortDropdown(open?: boolean) {
    this.sortDropdownOpen = open !== undefined ? open : !this.sortDropdownOpen;
    this.cdRef.detectChanges();
  }

  public setSortBy(sortBy: SortBy) {
    this.sortBy = sortBy;
    this.setVisibleItems();
  }

  private setVisibleItems(query: string = this.queryControl.value) {
    const sortedItems = [...this.allItems].sort(this.getSortFunction.bind(this));

    this.visibleItems = query
      ? sortedItems.filter(item => item.title.toLowerCase().includes(query))
      : [...sortedItems];

    this.cdRef.detectChanges();
  }

  private fillDescriptionFragments(item: Requisition): RequisitionListItem {
    const locationText = this.requisitionsCommonTexts.getLocationText(item);
    const jobOpeningsText = this.requisitionsCommonTexts.getJobOpeningsText(item.openings);
    return {
      ...item,
      departmentName: item.departmentId ? this.departmentMap[item.departmentId]?.name || '' : '',
      statusName: this.i18n.translate(`app.requisitions.status.${item.status}`),
      jobOpeningsText,
      locationText,
    };
  }

  private getSortFunction(a: RequisitionListItem, b: RequisitionListItem): number {
    switch (this.sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'statusName':
        return statusCompare(a, b);
      default:
        return 0;
    }
  }
}
